import {
    LimaRouteProps,
    ResponsivePictureLayout,
    UniformRouteProps,
} from '@news-mono/component-library'
import { layout } from '../../App.routing'
import {
    CardExtensionProps,
    Extensions,
    returnEightCardExtensionProps,
    returnFourCardExtensionProps,
    returnOverrideCardProps,
    returnSevenCardExtensionProps,
    WrappedCardProps,
} from './get-themed-segments-helpers'
import { Features } from '@news-mono/web-common'

type Curations =
    | 'afl-grand-final'
    | 'commonwealth-games'
    | 'kings-coronation'
    | '190-years-of-the-west'
    | 'go-matildas'
    | 'thewest' // external curation on TheNightly
    | 'new-york-times'

export type ThemeOverrides =
    | 'grand-final'
    | 'grand-final-freo'
    | 'commonwealth-games'
    | 'kings-coronation'
    | '190-years-of-the-west'
    | 'go-matildas'
    | 'thenightly-curation'
    | 'new-york-times-curation'

export type layouts = '4-card' | '8-card' | '7-card' | '6-card' | '5-card'

interface cardLayoutProps {
    featureToggle?: Features
    curation: Curations
    themeOverride: ThemeOverrides
    hideHeadingLink?: boolean
    cardExtensionProps?: Extensions
}

const fourCardLayout = ({
    featureToggle,
    curation,
    themeOverride,
    cardExtensionProps,
}: cardLayoutProps) => {
    const extensions = returnFourCardExtensionProps(cardExtensionProps)

    return layout.nestedComposition({
        type: 'thor',
        feature: featureToggle,
        props: {
            ...returnOverrideCardProps({
                cardLayout: '4-card',
                card: 'thor',
                curation,
                themeOverride,
                overrideProps: extensions.thorProps,
            }),
        },
        contentAreas: {
            main: [
                layout.component({
                    type: 'lima',
                    props: {
                        ...returnOverrideCardProps<
                            WrappedCardProps<LimaRouteProps>
                        >({
                            cardLayout: '4-card',
                            card: 'lima',
                            curation,
                            themeOverride,
                            overrideProps: extensions.limaProps,
                        }),
                    },
                }),
            ],
        },
    })
}

const fiveCardLayout = ({
    featureToggle,
    curation,
    themeOverride,
}: cardLayoutProps) => {
    return layout.nestedComposition({
        type: 'thor',
        feature: featureToggle,
        props: {
            ...returnOverrideCardProps({
                cardLayout: '5-card',
                card: 'thor',
                curation,
                themeOverride,
                overrideProps: {},
            }),
        },
        contentAreas: {
            main: [
                layout.component({
                    type: 'uniform',
                    props: {
                        ...returnOverrideCardProps<
                            WrappedCardProps<UniformRouteProps>
                        >({
                            cardLayout: '5-card',
                            card: 'uniform',
                            curation,
                            themeOverride,
                            overrideProps: undefined,
                        }),
                    },
                }),
            ],
        },
    })
}

const sevenCardLayout = ({
    featureToggle,
    curation,
    themeOverride,
    cardExtensionProps,
}: cardLayoutProps) => {
    const props = returnSevenCardExtensionProps(cardExtensionProps)

    return layout.nestedComposition({
        type: 'thor',
        feature: featureToggle,
        props: {
            ...returnOverrideCardProps({
                cardLayout: '7-card',
                card: 'thor',
                curation,
                themeOverride,
                overrideProps: props.thorProps,
            }),
        },
        contentAreas: {
            main: [
                layout.component({
                    type: 'sierra',
                    props: {
                        ...returnOverrideCardProps({
                            cardLayout: '7-card',
                            card: 'sierra',
                            curation,
                            themeOverride,
                            overrideProps: props.sierraProps,
                        }),
                    },
                }),
                layout.component({
                    type: 'lima',
                    props: {
                        ...returnOverrideCardProps({
                            cardLayout: '7-card',
                            card: 'lima',
                            curation,
                            themeOverride,
                            overrideProps: props.limaProps,
                        }),
                    },
                }),
            ],
        },
    })
}

const eightCardLayout = ({
    featureToggle,
    curation,
    themeOverride,
    cardExtensionProps,
}: cardLayoutProps) => {
    const cardProps = returnEightCardExtensionProps(cardExtensionProps)

    return layout.nestedComposition({
        type: 'thor',
        feature: featureToggle,
        props: {
            ...returnOverrideCardProps({
                cardLayout: '8-card',
                card: 'thor',
                curation,
                themeOverride,
                overrideProps: cardProps.thorProps,
            }),
        },
        contentAreas: {
            main: [
                layout.component({
                    type: 'sierra',
                    props: {
                        ...returnOverrideCardProps({
                            cardLayout: '8-card',
                            card: 'sierra',
                            curation,
                            themeOverride,
                            overrideProps: cardProps.sierraProps,
                        }),
                    },
                }),
                layout.nestedComposition({
                    type: 'thor',
                    props: {
                        hasBackgroundFill: false,
                        containerWidth: '100%',
                        verticalGutters: false,
                        horizontalGutters: false,
                    },
                    contentAreas: {
                        main: [
                            layout.nestedComposition({
                                type: 'janus',
                                props: {
                                    ...returnOverrideCardProps({
                                        cardLayout: '8-card',
                                        card: 'janus',
                                        curation,
                                        themeOverride,
                                        overrideProps: cardProps.janusProps,
                                    }),
                                },
                                contentAreas: {
                                    left: [
                                        layout.component({
                                            type: 'lima',
                                            props: {
                                                ...returnOverrideCardProps({
                                                    cardLayout: '8-card',
                                                    card: 'lima-left',
                                                    curation,
                                                    themeOverride,
                                                    overrideProps:
                                                        cardProps.leftLimaProps,
                                                }),
                                            },
                                        }),
                                    ],
                                    right: [
                                        layout.component({
                                            type: 'lima',
                                            props: {
                                                ...returnOverrideCardProps({
                                                    cardLayout: '8-card',
                                                    card: 'lima-right',
                                                    curation,
                                                    themeOverride,
                                                    overrideProps:
                                                        cardProps.rightLimaProps,
                                                }),
                                            },
                                        }),
                                    ],
                                },
                            }),
                        ],
                    },
                }),
            ],
        },
    })
}

export const sixCardLayout = ({
    featureToggle,
    curation,
    themeOverride,
}: cardLayoutProps) => {
    return layout.nestedComposition({
        type: 'thor',
        feature: featureToggle,
        props: {
            hasBackgroundFill: true,
            containerWidth: '100%',
            verticalGutters: false,
            horizontalGutters: true,
            verticalSpacing: 'lg',
            themeOverride: themeOverride,
        },
        contentAreas: {
            main: [
                layout.component({
                    type: 'sierra',
                    props: {
                        verticalSpacing: 'gridGap',
                        fixedRatios: ['16:9'],
                        heroImageLayout:
                            ResponsivePictureLayout.ObjectFitContain,
                        cardOrientation: {
                            type: 'Landscape-Portrait',
                            maxBreakpoint: 'md',
                        },
                        cardLayout: [
                            'the-west-hero',
                            'teaser-xsVisible-mdHidden-Image',
                        ],
                        overrideRestrictedVideoPlayback: true,
                        dataDefinitionArgs: {
                            type: 'curation',
                            name: curation,
                            offset: 0,
                            pageSize: 2,
                        },
                    },
                }),

                layout.component({
                    type: 'lima',
                    props: {
                        cardOrientation: {
                            type: 'Landscape-Portrait',
                        },
                        cardLayout: [
                            ['teaserWithBackground'],
                            ['teaserWithBackground'],
                            ['teaserWithBackground'],
                            ['teaserWithBackground'],
                        ],
                        intermediateColumns: 2,
                        finalColumns: 4,
                        dataDefinitionArgs: {
                            type: 'curation',
                            name: curation,
                            offset: 2,
                            pageSize: 4,
                        },
                        hasBorder: false,
                    },
                }),
            ],
        },
    })
}

export function getThemedSegment(props: {
    featureToggle?: Features
    layoutVersion: layouts
    curation: Curations
    themeOverride: ThemeOverrides
    cardExtensionProps?: CardExtensionProps
}) {
    switch (props.layoutVersion) {
        case '7-card':
            return sevenCardLayout({
                featureToggle: props.featureToggle,
                curation: props.curation,
                themeOverride: props.themeOverride,
                cardExtensionProps:
                    props.cardExtensionProps?.sevenCardExtension,
            })
        case '8-card':
            return eightCardLayout({
                featureToggle: props.featureToggle,
                curation: props.curation,
                themeOverride: props.themeOverride,
                cardExtensionProps:
                    props.cardExtensionProps?.eightCardExtension,
            })
        case '6-card':
            return sixCardLayout({
                featureToggle: props.featureToggle,
                curation: props.curation,
                themeOverride: props.themeOverride,
                cardExtensionProps:
                    props.cardExtensionProps?.eightCardExtension,
            })
        case '5-card':
            return fiveCardLayout({
                featureToggle: props.featureToggle,
                curation: props.curation,
                themeOverride: props.themeOverride,
            })

        case '4-card':
        default:
            return fourCardLayout({
                featureToggle: props.featureToggle,
                curation: props.curation,
                themeOverride: props.themeOverride,
                cardExtensionProps: props.cardExtensionProps?.fourCardExtension,
            })
    }
}
